import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type BookmakersConfig } from '@cx-web/api-clients/site-config-api/types';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { type RepubIs } from '@cx-web/utils';
import { Avatar, AvatarImage } from '@fpsm/cxy-components/Avatar';
import { ChevronRight } from 'lucide-react';

import { type Repub, REPUBS } from '@cx-modules/constants';

import { BetButton } from '~/apps/product-plus/components/bet-button/BetButton.tsx';
import teamPlaceholder from '~/apps/product-plus/components/BettingInsights/components/placeholders/team-placeholder.svg';
import {
  trackMatchupAnalysisClick,
  trackMatchupsBetClick,
} from '~/apps/product-plus/components/BettingInsights/tracking';
import { type MatchupCentreResult } from '~/apps/product-plus/configuration/types';
import { formatDate } from '~/apps/product-plus/configuration/utils/formatDate';
import useMedia from '~/hooks/use-media';
import { getMatchupTeamInfo } from '~/utils/getMatchupTeamInfo';

import { type Line } from '../types';

import styles from './styles/MatchupCard.module.css';

export interface MatchupCardProps {
  bookmakers: BookmakersConfig;
  content: MatchupCentreResult;
  isLocked?: boolean;
  isPreview?: boolean;
  repub: Repub;
  repubIs: RepubIs;
  repubPath: string;
  resourcesUrl?: string;
}

type MarketItem = MatchupCentreResult['markets'][number];
type BetItem = MarketItem['bets'][number];

interface MarketGroup {
  moneyline?: MarketItem;
  other?: MarketItem;
  spread?: MarketItem;
  total?: MarketItem;
}

type MarketType = keyof MarketGroup;

const getMarketType = (marketDisplayName: string): MarketType => {
  const lowerName = marketDisplayName.toLowerCase();
  if (lowerName.includes('spread') || lowerName.includes('handicap')) return 'spread';
  if (lowerName.includes('moneyline') || lowerName === 'win market') return 'moneyline';
  if (lowerName.includes('total') || lowerName.includes('over/under')) return 'total';
  return 'other';
};

const getTotalBetLabel = (line?: Line | null): string => {
  const firstLetter = line?.sign ? line.sign.charAt(0).toUpperCase() : '';
  return firstLetter && line?.absolute ? `${firstLetter} ${line.absolute}` : '';
};

const TeamBadge = ({
  badgeUrl,
  size = 'small',
  teamName,
}: {
  badgeUrl?: string | null;
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  teamName: string;
}): JSX.Element => {
  return (
    <Avatar size={size}>
      <AvatarImage alt={teamName || 'Team logo'} className={styles.teamBadge} src={badgeUrl || teamPlaceholder} />
    </Avatar>
  );
};

const SoccerBetButton = ({
  bet,
  bookmakers,
  isLocked,
  onClick,
  repubIs,
}: {
  bet: BetItem | null | undefined;
  bookmakers: BookmakersConfig;
  isLocked: boolean;
  onClick?: () => void;
  repubIs: RepubIs;
}): JSX.Element => {
  if (!bet) {
    return <div className={styles.noBet}>-</div>;
  }

  return (
    <BetButton
      bet={bet}
      bookieColour={bookmakers[bet.bookmakerCode]?.brandColour || 'black'}
      bookieLogo={bookmakers[bet.bookmakerCode]?.logoSmall}
      bookieName={bookmakers[bet.bookmakerCode]?.bookmakerName}
      buttonClassName={styles.soccerBetButton}
      hideBookieLogo={repubIs.GZ}
      locked={isLocked}
      onClick={onClick}
      showLabel={false}
      unformattedOdds={bet}
    />
  );
};

export const MatchupCard = ({
  bookmakers,
  content,
  isLocked = false,
  isPreview = false,
  repub,
  repubIs,
  repubPath,
}: MatchupCardProps) => {
  const { isDesktop } = useMedia();
  const isDesktopLayout = isDesktop;
  const repubConfig = REPUBS[repub];
  const plusConfig = REPUB_CONFIG[repub]?.plus;
  const matchCenterPath = plusConfig?.matchupCentre?.matchCenterPath || 'match-centre';
  const { t } = useTranslation();

  const formattedEventTime = useMemo(() => {
    const date = formatDate(content.subeventStartTime, 'EEE MMM d', repubConfig);
    const time = formatDate(content.subeventStartTime, 'h:mmaaa', repubConfig);
    if (repubIs.OC) return `${date} | ${time}`;
    const tz = formatDate(content.subeventStartTime, 'zzz', repubConfig);
    return `${date} | ${time} ${tz}`;
  }, [content.subeventStartTime, repubConfig, repubIs.OC]);

  const hasDrawOption = useMemo(
    () => content.markets?.some(market => market.bets.some(bet => bet.betName === 'Draw')) ?? false,
    [content.markets],
  );

  const { fallbackTeamNames, leftTeam, matchupString, rightTeam } = getMatchupTeamInfo({
    categoryGroupName: content.categoryGroupName,
    subeventName: content.subeventName,
    teamData: content.teamData,
  });

  const leftTeamName = leftTeam?.name || fallbackTeamNames.left;
  const leftTeamShortName = leftTeam?.shortName || fallbackTeamNames.left;

  const rightTeamName = rightTeam?.name || fallbackTeamNames.right;
  const rightTeamShortName = rightTeam?.shortName || fallbackTeamNames.right;

  const handleBetButtonClick = (bet: BetItem, market: MarketItem) => {
    trackMatchupsBetClick({
      bestOdds: bet.oddsAmerican,
      bookieName: bookmakers[bet.bookmakerCode]?.bookmakerName ?? '',
      evPercentage: bet.expectedValue,
      league: content.categoryName,
      market: market.marketDisplayName,
      playerOrTeam: bet.betName,
      sport: content.categoryGroupName,
      subevent: content.subeventName,
    });
  };

  const renderSoccerLayout = (): JSX.Element => {
    const market = content.markets.find(m => m.bets.some(bet => bet.betName === 'Draw'));
    if (!market) return <></>;

    let homeBet = market.bets.find(bet => bet.teamType === 'home');
    let awayBet = market.bets.find(bet => bet.teamType === 'away');
    const drawBet = market.bets.find(bet => bet.betName === 'Draw');

    if (!homeBet || !awayBet) {
      homeBet = homeBet || market.bets.find(bet => bet.betName === leftTeamName || bet.betName === leftTeamShortName);
      awayBet = awayBet || market.bets.find(bet => bet.betName === rightTeamName || bet.betName === rightTeamShortName);
    }

    const eventInfo = !isPreview ? `${matchupString} | ${formattedEventTime}` : null;

    return (
      <>
        {isDesktopLayout ? (
          <div className={styles.header}>
            <div className={styles.eventInfo} data-testid="event-info">
              {eventInfo}
            </div>
            <div className={styles.marketLabel}>{t('CX_PLUS_HOME')}</div>
            <div className={styles.marketLabel}>{t('CX_PLUS_DRAW')}</div>
            <div className={styles.marketLabel}>{t('CX_PLUS_AWAY')}</div>
          </div>
        ) : (
          <>
            {!isPreview && (
              <div className={styles.mobileHeader}>
                <div className={styles.eventInfo} data-testid="event-info">
                  {eventInfo}
                </div>
              </div>
            )}
            <div className={styles.mobileMarketLabels}>
              <div className={styles.teamColumn}></div>
              <div className={styles.marketLabel}>{t('CX_PLUS_HOME')}</div>
              <div className={styles.marketLabel}>{t('CX_PLUS_DRAW')}</div>
              <div className={styles.marketLabel}>{t('CX_PLUS_AWAY')}</div>
            </div>
          </>
        )}

        <div className={styles.marketGrid}>
          <div className={styles.soccerTeamRow}>
            <div className={styles.teamColumn}>
              <div className={styles.soccerStackedTeams}>
                <div className={styles.soccerStackedTeam} data-testid="left-team">
                  <TeamBadge
                    badgeUrl={leftTeam?.badgeUrl}
                    size={isDesktopLayout ? 'small' : 'xsmall'}
                    teamName={leftTeamName}
                  />
                  <span className={styles.teamName}>{isDesktop ? leftTeamName : leftTeamShortName}</span>
                </div>
                <div className={styles.soccerStackedTeam} data-testid="right-team">
                  <TeamBadge
                    badgeUrl={rightTeam?.badgeUrl}
                    size={isDesktopLayout ? 'small' : 'xsmall'}
                    teamName={rightTeamName}
                  />
                  <span className={styles.teamName}>{isDesktop ? rightTeamName : rightTeamShortName}</span>
                </div>
              </div>
            </div>
            <div className={styles.betCell} data-testid="home-bet">
              <SoccerBetButton
                bet={homeBet}
                bookmakers={bookmakers}
                isLocked={isLocked}
                onClick={homeBet ? () => handleBetButtonClick(homeBet, market) : undefined}
                repubIs={repubIs}
              />
            </div>
            <div className={styles.betCell} data-testid="draw-bet">
              <SoccerBetButton
                bet={drawBet}
                bookmakers={bookmakers}
                isLocked={isLocked}
                onClick={drawBet ? () => handleBetButtonClick(drawBet, market) : undefined}
                repubIs={repubIs}
              />
            </div>
            <div className={styles.betCell} data-testid="away-bet">
              <SoccerBetButton
                bet={awayBet}
                bookmakers={bookmakers}
                isLocked={isLocked}
                onClick={awayBet ? () => handleBetButtonClick(awayBet, market) : undefined}
                repubIs={repubIs}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderGeneralSportsLayout = (): JSX.Element => {
    const marketGroups: MarketGroup = {};
    content.markets.forEach(market => {
      const type = getMarketType(market.marketDisplayName);
      marketGroups[type] = market;
    });

    const getTeamBet = (market: MarketItem | undefined, teamType: 'home' | 'away'): BetItem | null => {
      if (!market) return null;
      const teamName = teamType === 'home' ? rightTeamName : leftTeamName;

      return (
        market.bets.find(bet => bet.teamType === teamType) ||
        market.bets.find(bet => bet.betName.includes(teamName)) ||
        null
      );
    };

    const renderTeamBet = (marketType: 'spread' | 'moneyline', teamType: 'home' | 'away'): JSX.Element => {
      const market = marketType === 'spread' ? marketGroups.spread : marketGroups.moneyline;
      const bet = getTeamBet(market, teamType);

      if (!bet || !market) {
        return <div className={styles.noBet}>-</div>;
      }

      const baseProps = {
        bet,
        bookieColour: bookmakers[bet.bookmakerCode]?.brandColour || 'black',
        bookieLogo: bookmakers[bet.bookmakerCode]?.logoSmall,
        bookieName: bookmakers[bet.bookmakerCode]?.bookmakerName,
        hideBookieLogo: repubIs.GZ,
        locked: isLocked,
        onClick: () => handleBetButtonClick(bet, market),
        showLabel: false,
        unformattedOdds: bet,
      };

      const spreadProps =
        marketType === 'spread' && bet.line
          ? {
              label: bet.line.name,
              line: `${bet.line.sign}${bet.line.absolute}`,
            }
          : {};

      return <BetButton {...baseProps} {...spreadProps} />;
    };

    const renderTotalBet = (betType: 'over' | 'under'): JSX.Element => {
      const market = marketGroups.total;
      const bet = market?.bets.find(bet => bet.betName.toLowerCase().includes(betType));

      if (!bet || !market) {
        return <div className={styles.noBet}>-</div>;
      }

      const label = getTotalBetLabel(bet.line);

      return (
        <BetButton
          bet={bet}
          bookieColour={bookmakers[bet.bookmakerCode]?.brandColour || 'black'}
          bookieLogo={bookmakers[bet.bookmakerCode]?.logoSmall}
          bookieName={bookmakers[bet.bookmakerCode]?.bookmakerName}
          hideBookieLogo={repubIs.GZ}
          line={label}
          locked={isLocked}
          onClick={() => handleBetButtonClick(bet, market)}
          showLabel={false}
          unformattedOdds={bet}
        />
      );
    };

    const eventInfo = !isPreview ? `${matchupString} | ${formattedEventTime}` : null;

    return (
      <>
        {isDesktop ? (
          <div className={styles.header}>
            <div className={styles.eventInfo} data-testid="event-info">
              {eventInfo}
            </div>
            <div className={styles.marketLabel}>{t('CX_PLUS_SPREAD_MARKET_TITLE')}</div>
            <div className={styles.marketLabel}>{t('CX_PLUS_MONEYLINE_MARKET_TITLE')}</div>
            <div className={styles.marketLabel}>{t('CX_PLUS_TOTAL_MARKET_TITLE')}</div>
          </div>
        ) : (
          <>
            {!isPreview && (
              <div className={styles.mobileHeader}>
                <div className={styles.eventInfo} data-testid="event-info">
                  {eventInfo}
                </div>
              </div>
            )}
            <div className={styles.mobileMarketLabels}>
              <div className={styles.teamColumn}></div>
              <div className={styles.marketLabel}>{t('CX_PLUS_SPREAD_MARKET_TITLE')}</div>
              <div className={styles.marketLabel}>{t('CX_PLUS_MONEYLINE_MARKET_TITLE')}</div>
              <div className={styles.marketLabel}>{t('CX_PLUS_TOTAL_MARKET_TITLE')}</div>
            </div>
          </>
        )}

        <div className={styles.marketGrid}>
          <div className={styles.teamRow}>
            <div className={styles.teamColumn}>
              <div className={styles.teamContent} data-testid="left-team">
                <TeamBadge
                  badgeUrl={leftTeam?.badgeUrl}
                  size={isDesktopLayout ? 'small' : 'xsmall'}
                  teamName={leftTeamName}
                />
                <span className={styles.teamName}>{isDesktopLayout ? leftTeamName : leftTeamShortName}</span>
              </div>
            </div>
            <div className={styles.betCell} data-testid="away-spread">
              {renderTeamBet('spread', 'away')}
            </div>
            <div className={styles.betCell} data-testid="away-moneyline">
              {renderTeamBet('moneyline', 'away')}
            </div>
            <div className={styles.betCell} data-testid="over-total">
              {renderTotalBet('over')}
            </div>
          </div>

          <div className={styles.teamRow}>
            <div className={styles.teamColumn}>
              <div className={styles.teamContent} data-testid="right-team">
                <TeamBadge
                  badgeUrl={rightTeam?.badgeUrl}
                  size={isDesktopLayout ? 'small' : 'xsmall'}
                  teamName={rightTeamName}
                />
                <span className={styles.teamName}>{isDesktopLayout ? rightTeamName : rightTeamShortName}</span>
              </div>
            </div>
            <div className={styles.betCell} data-testid="home-spread">
              {renderTeamBet('spread', 'home')}
            </div>
            <div className={styles.betCell} data-testid="home-moneyline">
              {renderTeamBet('moneyline', 'home')}
            </div>
            <div className={styles.betCell} data-testid="under-total">
              {renderTotalBet('under')}
            </div>
          </div>
        </div>
      </>
    );
  };

  if (!content.markets || content.markets.length === 0) {
    return null;
  }

  return (
    <div className={styles.matchupCard}>
      {hasDrawOption ? renderSoccerLayout() : renderGeneralSportsLayout()}

      {!isPreview && (
        <div className={styles.matchupFooter}>
          <a
            className={styles.matchupAnalysisLink}
            href={`${repubPath}/plus/${matchCenterPath}/${content.subeventId}`}
            onClick={() =>
              trackMatchupAnalysisClick({
                league: content.categoryName,
                subevent: content.subeventName,
              })
            }
          >
            {t('CX_PLUS_MS_ANALYSIS')} <ChevronRight size={16} />
          </a>
        </div>
      )}
    </div>
  );
};

export default MatchupCard;
